import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { useLocation, Navigate } from 'react-router-dom';

interface AuthGateProps {
  children: React.ReactNode;
}

// Paths a signed-in user is allowed to visit WITHOUT having any brands yet.
// Anything else triggers a hard redirect to /brands/new so we don't render
// brand-scoped pages (My Creatives, Products, Calendar, etc.) with an
// empty active brand — those surfaces 400 because /api/gallery/posts +
// friends need an active brand.
//
// Add to this list if you build any new user page that should be reachable
// pre-onboarding (settings, profile, billing, etc.).
const BRAND_FREE_PATH_PREFIXES = [
  '/brands',           // /brands manage page + /brands/new wizard
  '/auth/',            // profile, change password, sign-out
  '/checkout',         // future Stripe flow
  '/connect-facebook', // OAuth page-picker — no brand needed to pick a Page
];

function isBrandFreePath(pathname: string): boolean {
  return BRAND_FREE_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

// Freemium (Sep 2026): 'free' is now a first-class in-app tier — new users get
// 20 render credits and go straight into onboarding + the app. There is NO
// plan-tier bounce here anymore. The vendor-cost-draining features (Ad
// Strategist, Ad Pilot, UGC Finder, competitor add/refresh) lock themselves at
// the feature level (upsell CTA -> /checkout) + a backend Pro gate; when a free
// user runs out of credits the generate flow 402s into the upgrade CTA. So
// AuthGate only enforces auth + email-verify + "has a brand yet".
export default function AuthGate({ children }: AuthGateProps) {
  const { user, loading: authLoading } = useAuth();
  const { brands, loading: brandLoading, loaded: brandLoaded, loadError: brandLoadError, emailUnverified, refresh: refreshBrands } = useBrand();
  const location = useLocation();

  // Phase 1: wait for auth check.
  if (authLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-surface">
        <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  // Phase 2: not signed in → bounce to sign-in, remember where they were
  // going so we can return them after sign-in succeeds.
  if (!user) {
    return <Navigate to="/auth/sign-in" replace state={{ from: location.pathname }} />;
  }

  // Phase 2.5: signed in but email not verified → bounce to the verify
  // holding page. The backend also 403s unverified users (the real gate);
  // this is the UX layer so they land somewhere sensible instead of an
  // error. /auth/verify-email is a public route (not wrapped by AuthGate),
  // so there's no redirect loop. Only email/password users are gated —
  // phone users have no email (user.email === '') and Google users come
  // pre-verified, so both skip this.
  if (user.email && !user.emailVerified) {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // Phase 3: signed in. Wait for the brand list to load so we can detect
  // "no brands yet → run the onboarding wizard". Skip the wait on brand-
  // free paths (the wizard doesn't need brands to load before rendering).
  if (brandLoading && !isBrandFreePath(location.pathname)) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-surface">
        <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  // Phase 3.4: the brands fetch returned 403 email_not_verified even after
  // useBrand force-refreshed the token (the common stale-token case is handled
  // silently there). The email genuinely isn't verified — a stale client-side
  // `emailVerified` let them past Phase 2.5. Route to the verify page rather
  // than the misleading "network hiccup" screen. /auth/verify-email is public
  // (not AuthGate-wrapped) so there's no redirect loop.
  if (emailUnverified) {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // Phase 3.5: the brands fetch failed (after retries) — NOT a genuine
  // "0 brands". Show a retry instead of falling through to the onboarding
  // redirect, which would strand a brand-owning user in the "Add a brand"
  // wizard (the reported "always lands on Add A Brand" bug when the fetch
  // errors transiently right after sign-in).
  if (brandLoadError && !isBrandFreePath(location.pathname)) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-4 bg-surface px-6 text-center">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <i className="ri-wifi-off-line text-2xl" />
        </div>
        <p className="text-dark font-semibold">Couldn’t load your brands.</p>
        <p className="text-dark-muted text-sm max-w-sm">
          A network hiccup got in the way. Your brands are safe — give it another try.
        </p>
        <button
          onClick={() => { void refreshBrands(); }}
          className="px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  // Phase 4: signed in, brand list loaded SUCCESSFULLY, brand-scoped path,
  // but genuinely zero brands → route to the onboarding wizard. Gated on
  // `brandLoaded` so an empty list from a not-yet-completed / errored fetch
  // can't trigger a false onboarding redirect for a user who owns brands.
  // Freshly-signed-up users land here; users with ≥1 brand skip past to My
  // Creatives for their active (most-recently-activated) brand.
  if (brandLoaded && brands.length === 0 && !isBrandFreePath(location.pathname)) {
    return <Navigate to="/brands/new" replace />;
  }

  return <>{children}</>;
}
